import { MILESTONE_REVIEW_TYPE } from './constants';

/**
 * The four fixed stages of the Async approval flow shown at the top of the
 * Edit Milestone modal. Values are 1-based so they map directly to the step
 * numbers rendered in the UI.
 */
export enum ApprovalFlowStage {
  ADD_EDIT = 1,
  SEND_FOR_APPROVAL = 2,
  PENDING = 3,
  APPROVED = 4
}

/** Ordered step definitions rendered by the flow component. */
export const APPROVAL_FLOW_STEPS: ReadonlyArray<{
  stage: ApprovalFlowStage;
  label: string;
}> = [
  { stage: ApprovalFlowStage.ADD_EDIT, label: 'Add/Edit' },
  { stage: ApprovalFlowStage.SEND_FOR_APPROVAL, label: 'Send for Approval' },
  { stage: ApprovalFlowStage.PENDING, label: 'Pending' },
  { stage: ApprovalFlowStage.APPROVED, label: 'Approved' }
];

/** Backend status (lowercased) → active flow stage. Only these statuses show the flow. */
const STATUS_TO_STAGE: Record<string, ApprovalFlowStage> = {
  sent: ApprovalFlowStage.SEND_FOR_APPROVAL,
  sent_pending: ApprovalFlowStage.PENDING,
  approved: ApprovalFlowStage.APPROVED,
  approved_async: ApprovalFlowStage.APPROVED,
  re_approved: ApprovalFlowStage.APPROVED,
  re_approved_async: ApprovalFlowStage.APPROVED
};

/**
 * Not Approved has no step of its own in the 4-stage flow indicator (Add/Edit →
 * Send for Approval → Pending → Approved) — it's deliberately absent from
 * {@link STATUS_TO_STAGE} so the stepper never renders a misleading "Approved"
 * checkmark for a rejected milestone. It's still a *concluded* Send-for-Approval
 * outcome though (an email genuinely went out and the approver replied), so it
 * needs the same read-only lock + reopen-to-Sent + "show what was sent"
 * treatment as Approved/Re-Approved — see {@link isNotApprovedStatus}, used
 * alongside {@link isApprovedOrReApprovedStatus} everywhere that applies.
 */
const NOT_APPROVED_STATUSES = new Set(['not_approved', 'not_approved_async']);

/**
 * Whether the given status participates in the approval flow (sent /
 * sent - feedback pending / approved / re-approved / not approved, incl.
 * async variants). Single source of truth shared by the flow indicator and
 * the "Send for Approval" form so they stay in sync. Not Approved counts here
 * (so the read-only "Send for Approval" form still shows what was actually
 * sent) even though it has no stage of its own in {@link STATUS_TO_STAGE}.
 */
export function isApprovalFlowStatus(status?: string): boolean {
  const statusLower = (status || '').toLowerCase();
  return (
    statusLower in STATUS_TO_STAGE || NOT_APPROVED_STATUSES.has(statusLower)
  );
}

/**
 * Whether the given status is a concluded "Not Approved" outcome (incl. async
 * variant) — the approver replied Not Approved to a genuine Send-for-Approval
 * request. Reuses {@link NOT_APPROVED_STATUSES} so this stays in sync with
 * {@link isApprovalFlowStatus}.
 */
export function isNotApprovedStatus(status?: string): boolean {
  return NOT_APPROVED_STATUSES.has((status || '').toLowerCase());
}

export interface AsyncSentSendForApprovalInput {
  /** Milestone status (backend value). */
  status?: string;
  /** Milestone review type (backend value). */
  reviewType?: string;
  /** "Send for Approval" checkbox / persisted flag — accepts the API's boolean or "true"/"false" string form. */
  sendForApproval?: unknown;
}

/**
 * Whether this save/response represents the actual Async "send for approval"
 * action — status `sent`, review type Async, and the Send-for-Approval flag
 * set. Single source of truth shared by:
 *   - {@link EditMilestoneModal} to decide whether `subject`/`cc`/`ccUid`/
 *     `noteToApprover`/`sendForApproval` belong in the PATCH payload, and
 *   - {@link MilestoneMatrixFromAPI}'s save handler to decide whether to check
 *     the PATCH response's `emailSent` flag and show the Save-and-Send toasts.
 */
export function isAsyncSentSendForApprovalAttempt({
  status,
  reviewType,
  sendForApproval
}: AsyncSentSendForApprovalInput): boolean {
  return (
    sendForApproval === true &&
    reviewType === MILESTONE_REVIEW_TYPE.ASYNC &&
    String(status || '').toLowerCase() === 'sent'
  );
}

export interface ManualStatusChangeInput {
  /** Status the user just picked from the dropdown (backend value). */
  status?: string;
  /** Milestone review type (backend value). */
  reviewType?: string;
}

/**
 * Whether the user manually picking Approved / Re-Approved / Not Approved
 * (Async milestone) from the status dropdown should reset the persisted
 * `sendForApproval` flag.
 *
 * Only fires for a genuine manual status change — call this from the status
 * dropdown's own handler, not from status changes that happen as a side
 * effect elsewhere (e.g. review type switch resetting status to a default).
 * Once a milestone reaches any of these concluded outcomes, any earlier
 * Send-for-Approval state is stale, so the checkbox/flow should clear rather
 * than keep showing an outdated Sent/Pending indicator.
 *
 * Not Approved matters here just as much as Approved/Re-Approved: if the user
 * checks "Send for Approval" at `sent` but manually picks "Not Approved"
 * before ever actually sending (no email went out), leaving `sendForApproval`
 * `true` would make `isApprovalReadOnly` read that as a genuine concluded
 * outcome and lock the whole form — see `isNotApprovedStatus`'s doc comment.
 *
 * Reuses {@link isApprovedOrReApprovedStatus} / {@link isNotApprovedStatus} so
 * this stays in sync with what those statuses mean everywhere else.
 */
export function shouldClearSendForApprovalOnManualStatusChange({
  status,
  reviewType
}: ManualStatusChangeInput): boolean {
  if (reviewType !== MILESTONE_REVIEW_TYPE.ASYNC) {
    return false;
  }
  return isApprovedOrReApprovedStatus(status) || isNotApprovedStatus(status);
}

/**
 * Whether the given status is one of the Approved / Re-Approved stage statuses
 * (incl. async variants). Reuses {@link STATUS_TO_STAGE} so this stays in sync
 * with the flow indicator's own notion of "approved".
 */
export function isApprovedOrReApprovedStatus(status?: string): boolean {
  return (
    STATUS_TO_STAGE[(status || '').toLowerCase()] === ApprovalFlowStage.APPROVED
  );
}

/**
 * Whether the Edit Milestone modal's status dropdown should stay restricted
 * to just "Sent" (plus the status being reopened from) despite the rest of
 * the form being read-only, so a concluded milestone can be reopened for
 * another round of feedback.
 *
 * Only applies to a milestone that actually went through a real
 * Send-for-Approval round: `persistedReviewType` must be Async AND
 * `persistedSendForApproval` must be `true`, in addition to `persistedStatus`
 * being concluded (Approved/Re-Approved/Not-Approved). A Meeting-reviewType
 * milestone, or an Async milestone approved/rejected manually without ever
 * using Send for Approval, has no email workflow to protect — its status
 * dropdown should behave exactly as a normal, fully-open dropdown, same as
 * before this reopen-gating existed.
 *
 * All three of `persistedStatus`/`persistedReviewType`/`persistedSendForApproval`
 * must be the last **server-confirmed** values (e.g. straight off the
 * `milestone` prop), never the live, possibly-unsaved `formData` equivalents.
 * `handleReopenToSent` sets `formData.status` to `sent` AND clears
 * `formData.sendForApproval` to `false` before anything is saved — gating on
 * either of those instead would make this `false` the instant "Sent" is
 * picked, unlocking every other status option before the reopen is ever
 * persisted, letting the user jump straight to a different concluded outcome
 * (e.g. Approved) without a new Send-for-Approval round ever happening.
 *
 * Also `false` while `isSaving`, so the in-flight window of an actual reopen
 * save (before a fresh `milestone` prop lands) still fully locks the status
 * field too, not just every other field.
 *
 * `liveReviewType` (optional) is the ONE exception to "persisted values only"
 * above, and is a different kind of check — not self-referential the way
 * status/sendForApproval are. If the user live-edits "Type of Review" away
 * from Async in the same unsaved session, the restriction must lift
 * immediately: the dropdown's rendered options switch to the target review
 * type's own status vocabulary (e.g. Meeting's TARGET/SCHEDULED/APPROVED,
 * which has no `_async`/`sent` literals at all), so comparing them against a
 * still-Async `persistedStatus` produces nonsense matches instead of a
 * correctly fully-open dropdown. Left `undefined` (e.g. by existing callers
 * that don't pass it) this check is skipped entirely, preserving prior
 * behavior.
 */
export function canReopenStatusField(
  persistedStatus?: string,
  persistedReviewType?: string,
  persistedSendForApproval?: boolean,
  isSaving?: boolean,
  liveReviewType?: string
): boolean {
  if (isSaving) {
    return false;
  }
  if (persistedReviewType !== MILESTONE_REVIEW_TYPE.ASYNC) {
    return false;
  }
  if (
    liveReviewType !== undefined &&
    liveReviewType !== MILESTONE_REVIEW_TYPE.ASYNC
  ) {
    return false;
  }
  if (!persistedSendForApproval) {
    return false;
  }
  return (
    isApprovedOrReApprovedStatus(persistedStatus) ||
    isNotApprovedStatus(persistedStatus)
  );
}

/** Strips the Async suffix so e.g. `approved_async` and legacy `approved` compare equal. */
function stripAsyncSuffix(status?: string): string {
  return (status || '').toLowerCase().replace(/_async$/, '');
}

export interface StatusDropdownOptionInput {
  /** The status literal this dropdown row represents (e.g. `SENT`, `APPROVED_ASYNC`). */
  statusId: string;
  /** The live, possibly-unsaved status currently in the form. */
  formDataStatus?: string;
  /** The last server-confirmed status (the `milestone` prop), never `formDataStatus`. */
  persistedStatus?: string;
  /** {@link canReopenStatusField}'s result for this milestone. */
  canReopen: boolean;
}

export type StatusDropdownOptionAction =
  | 'reopenToSent'
  | 'undoReopen'
  | 'setStatus';

export interface StatusDropdownOptionResult {
  /** Whether this row represents the form's current (possibly unsaved) status — drives the checkmark. */
  isCurrentStatusEquivalent: boolean;
  /** Whether this row is enabled/clickable. */
  isOptionEnabled: boolean;
  /** What clicking this row (when enabled) should do. */
  action: StatusDropdownOptionAction;
}

/**
 * Decides a single status-dropdown row's enabled/checked state and click
 * action for the Edit Milestone modal.
 *
 * While reopening (`canReopen`), only three rows are real: the form's
 * current status (no-op, just closes the dropdown), "Sent" (commits
 * `handleReopenToSent`), and the exact status being reopened FROM —
 * `persistedStatus`, which is a cancel of the reopen (`handleUndoReopen`),
 * restoring what `handleReopenToSent` cleared, not a fresh manual
 * approve/reject. Every other row is disabled unless the persisted outcome
 * is Not Approved, which allows all status options. `undoReopen` only fires once
 * the reopen has actually been picked locally (`formDataStatus` is `sent`) —
 * if `formDataStatus` still equals `persistedStatus` (the reopen was never
 * clicked), picking that same row is just the ordinary current-status no-op.
 *
 * Outside `canReopen`, every row behaves as a plain status pick.
 */
export function resolveStatusDropdownOption({
  statusId,
  formDataStatus,
  persistedStatus,
  canReopen
}: StatusDropdownOptionInput): StatusDropdownOptionResult {
  const statusIdLower = statusId.toLowerCase();
  const isCurrentStatusEquivalent =
    (formDataStatus || '').toLowerCase() === statusIdLower ||
    stripAsyncSuffix(statusId) === stripAsyncSuffix(formDataStatus);
  const isSent = statusIdLower === 'sent';
  const isOriginalPersistedStatus =
    stripAsyncSuffix(statusId) === stripAsyncSuffix(persistedStatus);

  if (!canReopen) {
    return {
      isCurrentStatusEquivalent,
      isOptionEnabled: true,
      action: 'setStatus'
    };
  }

  const isOptionEnabled =
    isNotApprovedStatus(persistedStatus) ||
    isCurrentStatusEquivalent ||
    isSent ||
    isOriginalPersistedStatus;

  let action: StatusDropdownOptionAction = 'setStatus';
  if (isSent) {
    action = 'reopenToSent';
  } else if (
    isOriginalPersistedStatus &&
    (formDataStatus || '').toLowerCase() === 'sent'
  ) {
    action = 'undoReopen';
  }

  return { isCurrentStatusEquivalent, isOptionEnabled, action };
}

export function isInactiveApprovalReviewType(reviewType?: string): boolean {
  return (
    reviewType === MILESTONE_REVIEW_TYPE.TBD ||
    reviewType === MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED
  );
}

/** Current-cycle fields only; historical approval requests are not changed. */
export function getNewApprovalCycleState<
  T extends string | ApprovalCcContact[]
>(cc: T) {
  return {
    sendForApproval: false,
    emailSent: false,
    cc,
    ccUid: '',
    noteToApprover: '',
    approvedBy: '',
    reApprovedBy: '',
    notApprovedBy: '',
    originalApprovedOn: '',
    reApprovedOn: ''
  };
}

export interface ResolveOptimisticEmailSentInput {
  /** Milestone status before this save (the parent's last-confirmed status, e.g. `selectedMilestone.status`). */
  previousStatus?: string;
  /** Status this save is submitting (the PATCH payload's `status`), defaults to `previousStatus` when omitted. */
  nextStatus?: string;
  nextReviewType?: string;
  /** The value that would otherwise leak unchanged into an optimistic pre-PATCH merge (e.g. `selectedMilestone.emailSent`). */
  previousEmailSent?: boolean;
}

/**
 * Optimistic pre-PATCH `emailSent` value for a milestone save.
 *
 * The PATCH payload built by `EditMilestoneModal.handleSave` never includes
 * `emailSent` (it's server-computed and deliberately stripped before it
 * reaches the backend), so an optimistic merge like
 * `{...selectedMilestone, ...updatedData}` would otherwise silently carry
 * over whatever `emailSent` the PREVIOUS approval round left behind.
 *
 * That's correct while nothing about the Send-for-Approval state is
 * changing (e.g. editing an unrelated field on an already-Pending
 * milestone), but wrong the moment `status` is (re-)entering `sent` from
 * anything else — a brand new Send-for-Approval round can never
 * legitimately start out already emailed, regardless of what the last round
 * left behind. Without this guard, reopening an Approved/Not-Approved
 * milestone back to Sent and resubmitting (e.g. with a since-corrected due
 * date) optimistically jumps straight to the locked Pending stage before
 * the PATCH even resolves — and if that PATCH then fails validation (e.g. a
 * past due date, rejected before any persistence), nothing rolls this back,
 * permanently locking the modal with no way to fix and resubmit.
 *
 * Mirrors the same "fresh Sent cycle" reasoning as
 * {@link shouldClearSendForApprovalOnManualStatusChange} and
 * EditMilestoneModal's own `isReenteringAsyncSent`.
 * N/A and TBD also clear the local flag because approval is no longer applicable.
 */
export function resolveOptimisticEmailSent({
  previousStatus,
  nextStatus,
  nextReviewType,
  previousEmailSent
}: ResolveOptimisticEmailSentInput): boolean {
  if (isInactiveApprovalReviewType(nextReviewType)) return false;
  const isFreshSentEntry =
    String(nextStatus ?? previousStatus ?? '').toLowerCase() === 'sent' &&
    String(previousStatus || '').toLowerCase() !== 'sent';

  return isFreshSentEntry ? false : !!previousEmailSent;
}

/** A name/UID pair reconstructed from persisted approval recipients. */
export interface ApprovalCcContact {
  name: string;
  uid: string;
}

/**
 * Re-zip the persisted `cc` (comma-joined names) and `ccUid` (comma-joined uids)
 * strings — the shape sent to and returned by the backend — back into the
 * `{ name, uid }` contact array used by the modal's CC and approver fields.
 *
 * Splits both on commas, trims each entry, pairs them by index, and drops any
 * entry without a name. Missing UIDs stay empty, never inferred from names.
 * Keep empty UID slots so later recipients retain their positional mapping.
 */
export function parseCcContacts(
  cc?: string,
  ccUid?: string
): ApprovalCcContact[] {
  const names = (cc || '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const uids = (ccUid || '').split(',').map(part => part.trim());

  return names.map((name, index) => ({ name, uid: uids[index] || '' }));
}

export interface ComputeApprovalFlowStageInput {
  /** Live state of the "Send for Approval" checkbox. */
  sendForApproval: boolean;
  /** Milestone review type (backend value). */
  reviewType?: string;
  /** Milestone status (backend value). */
  status?: string;
  /**
   * Whether the Send-for-Approval email has actually gone out (round-tripped
   * from the API). The `sent_pending` status was retired; `status === 'sent'`
   * combined with `emailSent: true` is now the signal for the Pending stage.
   */
  emailSent?: boolean;
}

/**
 * Computes the active stage of the approval flow, always combining the
 * `sendForApproval` flag with the review type and status.
 *
 * The flow is shown ONLY when all three hold:
 *   1. `sendForApproval` checkbox is checked, AND
 *   2. review type is Async, AND
 *   3. status is one of sent / sent_pending / approved / re_approved.
 *
 * If any is false — including the checkbox being unchecked — returns `null`
 * and the flow is removed completely.
 *
 * Special case: `sent_pending` no longer comes from the backend. When status
 * is `sent` and `emailSent` is `true`, the stage is Pending rather than
 * Send for Approval.
 */
export function computeApprovalFlowStage({
  sendForApproval,
  reviewType,
  status,
  emailSent
}: ComputeApprovalFlowStageInput): ApprovalFlowStage | null {
  if (!sendForApproval) {
    return null;
  }

  if (reviewType !== MILESTONE_REVIEW_TYPE.ASYNC) {
    return null;
  }

  const statusLower = (status || '').toLowerCase();

  if (statusLower === 'sent' && emailSent) {
    return ApprovalFlowStage.PENDING;
  }

  return STATUS_TO_STAGE[statusLower] ?? null;
}
